<template>
  <div class="page-wrapper" ref="pageWrapper">
    <header class="header" ref="header">
      <div class="hamburger-menu control-btn" @click="toggleLeftPanel">
        <BaseIcon iconName="hamburger"></BaseIcon>
      </div>

      <div class="header-content">
        <h1 class="header-1">
          <img src="@/assets/logotext.png" :alt="$t('app.logoAlt')" class="logo" />
        </h1>
      </div>

      <div class="header-actions">
        <LanguageSwitcher />
        <div class="control-btn" @click="handleHomeButton">
          <BaseIcon iconName="bell"></BaseIcon>
        </div>
      </div>
    </header>

    <div class="main-content">
      <div
        class="overlay"
        v-if="isLeftPanelOpen && isMobile"
        @click="closeLeftPanel"
      ></div>

      <div class="left-panel" :class="{ open: isLeftPanelOpen }">
        <div class="nav-bar">
          <MenuCell iconName="home" to="/app/home" @click="closeLeftPanel">
            {{ $t('layout.navigation.home') }}
          </MenuCell>

          <MenuCell iconName="folder" to="/app/animations" @click="closeLeftPanel">
            {{ $t('layout.navigation.animations') }}
          </MenuCell>

          <MenuCell
            iconName="star"
            :locked="false"
            to="/app/favorites"
            @click="closeLeftPanel"
          >
            {{ $t('layout.navigation.favorites') }}
          </MenuCell>

          <MenuCell
            iconName="book"
            :locked="true"
            to="/app/tutorials"
            @click="closeLeftPanel"
          >
            {{ $t('layout.navigation.tutorials') }}
          </MenuCell>

          <div class="nav-separator"></div>

          <MenuCell iconName="key" to="/app/api-tokens" @click="closeLeftPanel">
            {{ $t('layout.navigation.pluginKeys') }}
          </MenuCell>
        </div>

        <div class="bottom-panel">
          <CreditsBar />
          <div class="email-container">
            <span class="email-text">{{ email }}</span>
            <BaseIcon
              iconName="dotHorizontal"
              @click="toggleContextMenu"
              class="bottom-panel-icon"
            >
            </BaseIcon>
          </div>
        </div>

        <ContextMenu ref="contextMenu" @logout="showLogoutConfirm = true" />
      </div>

      <div class="content">
        <router-view></router-view>
      </div>
    </div>
    <CookieConsent />

    <BaseModal
      :show="showLogoutConfirm"
      :title="$t('layout.logout.title')"
      :confirmText="$t('layout.logout.confirm')"
      :cancelText="$t('common.cancel')"
      :danger="true"
      @confirm="confirmLogout"
      @cancel="showLogoutConfirm = false"
    >
      {{ $t('layout.logout.message') }}
    </BaseModal>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from "vue";

import BaseIcon from "@/components/BaseComponents/BaseIcon.vue";
import MenuCell from "@/components/MenuCell.vue";
import ContextMenu from "@/components/ContextMenu.vue";
import CreditsBar from "@/components/CreditsBar.vue";
import CookieConsent from "@/components/CookieConsent.vue";
import BaseModal from "@/components/BaseComponents/BaseModal.vue";
import LanguageSwitcher from "@/components/LanguageSwitcher.vue";
import { useBreakpoint } from "@/composables/useBreakpoint";

import { useStore } from "vuex";

const store = useStore();
const email = store.getters.getEmail;

const { isMobile } = useBreakpoint();

const contextMenu = ref(null);
const header = ref(null);
const pageWrapper = ref(null);
const headerHeight = ref(0);
const paddingTop = ref(0);

const isLeftPanelOpen = ref(false);
const showLogoutConfirm = ref(false);

function confirmLogout() {
  showLogoutConfirm.value = false;
  store.dispatch("logout");
}

function toggleLeftPanel() {
  isLeftPanelOpen.value = !isLeftPanelOpen.value;
}

function closeLeftPanel() {
  isLeftPanelOpen.value = false;
}

function toggleContextMenu(event) {
  if (contextMenu.value) {
    contextMenu.value.showMenu(event);
  }
}

onMounted(async () => {
  await store.dispatch('fetchUserDetails');
  await store.dispatch('syncLocalePreference');
  await nextTick();
  updateDimensions();
  window.addEventListener("resize", updateDimensions);
});

function updateDimensions() {
  headerHeight.value = header.value.offsetHeight;
  paddingTop.value = parseFloat(getComputedStyle(pageWrapper.value).paddingTop);
}

onUnmounted(() => {
  window.removeEventListener("resize", updateDimensions);
});
</script>

<style scoped>
.page-wrapper {
  --app-page-gutter: 2vw;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  height: 100vh;
  box-sizing: border-box;
  padding-inline: var(--app-page-gutter);
  overflow: hidden;
}

.header {
  position: sticky;
  top: 0;
  z-index: var(--z-index-header);
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  flex-shrink: 0;
}

.header-content {
  display: flex;
  align-items: center;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.logo {
  height: 70px;
  width: auto;
  padding: 0 var(--spacing-sm-lg);
}

.hamburger-menu {
  display: none;
  cursor: pointer;
}

.main-content {
  display: flex;
  flex: 1;
  overflow: hidden;
  position: relative;
  min-height: 0;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--overlay-medium-dark);
  z-index: 2;
}

.left-panel {
  height: 87%;
  position: fixed;
  width: calc(20% - 2vw);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  padding-bottom: 2vh;
  z-index: 3;
  transition: transform var(--transition-normal);
  margin-bottom: var(--spacing-sm-lg);
}

.left-panel.open {
  transform: translateX(0);
  padding-right: var(--spacing-2xl);
}

.nav-bar {
  display: flex;
  flex-direction: column;
}

.nav-separator {
  height: 1px;
  background: var(--color-white-10);
  margin: var(--spacing-lg) 0;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: var(--spacing-lg) var(--spacing-xs);
  text-decoration: none;
  border-radius: var(--border-radius-xs);
  color: var(--color-white-40);
}

.reset-decoration {
  text-decoration: none;
}

.icon-padding {
  padding-right: var(--spacing-sm-lg);
}

.router-link-active {
  color: var(--color-white);
  background-color: var(--color-white-10);
}

.content {
  margin-left: 20%;
  margin-bottom: var(--spacing-sm-lg);
  padding-left: var(--app-page-gutter);
  width: calc(80% - 2vw);
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.bottom-panel {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-2xl);
}

.email-container {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--spacing-2xl);
  gap: var(--spacing-xs);
}

.email-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;
}

.bottom-panel-icon {
  cursor: pointer;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .hamburger-menu {
    display: flex;
  }

  .left-panel {
    width: 80%;
    transform: translateX(-120%);
    background-color: var(--color-background);
    top: 0;
    left: 0;
    bottom: 0;
    z-index: var(--z-index-modal);
    padding-top: 60px;
  }

  .content {
    margin-left: 0;
    width: 100%;
    transition: filter var(--transition-normal);
  }

  .overlay ~ .content {
    filter: blur(3px);
  }
}

@media (max-height: 600px) {
  .left-panel {
    height: 80vh;
  }
}
</style>
